import { classList } from '@findigs/helpers/src/helpers/class-list';
import { polymorphic } from '@findigs/helpers/src/helpers/polymorphic';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import type { LinkProps } from '../link';
import { Link } from '../link';
import { styles } from './link-icon.module.scss';

export type LinkIconProps = Omit<LinkProps, 'ref'> & {
  /**
   * JSX.Element
   */
  icon: JSX.Element;

  /** @default 'Small' */
  size?: 'Small' | 'Large';

  /** @default 'Horizontal' */
  orientation?: 'Horizontal' | 'Vertical';

  /** @default 'Start' */
  iconLocation?: 'Start' | 'End';
};

/**
 * Extends the `Link` component with icon support.
 * All `Link` props are forwarded.
 *
 * This component is [polymorphic](/docs/component-system-polymorphic-components--docs).
 */
export const LinkIcon = polymorphic(
  { element: 'a', forwardRef: true },
  (
    {
      icon,
      inverse: inverseProp,
      size = 'Small',
      iconLocation = 'Start',
      orientation = 'Horizontal',
      children,
      className,
      onMouseEnter,
      onMouseLeave,
      onMouseUp,
      ...props
    }: LinkIconProps,
    Component,
    forwardedRef
  ) => {
    const hasInverseClass =
      className?.toLowerCase().split(' ').includes('inverse') ?? false;
    const inverse = inverseProp || hasInverseClass;

    // here we prevent the child element from receiving pointer events,
    // receive those in the parent element, then forward them
    // to the child element (Link). This is so our hitbox
    // includes not only the text, but also the icon.

    const _onMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        const span = e.currentTarget?.querySelector('span');
        const ev = new Event('mouseenter', { bubbles: false });
        span?.dispatchEvent(ev);
        if (onMouseEnter) {
          onMouseEnter(e);
        }
      },
      [onMouseEnter]
    );

    const _onMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        const span = e.currentTarget?.querySelector('span');
        const ev = new Event('mouseleave', { bubbles: false });
        span?.dispatchEvent(ev);
        if (onMouseLeave) {
          onMouseLeave(e);
        }
      },
      [onMouseLeave]
    );

    const _onMouseUp = useCallback(
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        const span = e.currentTarget?.querySelector('span');
        const ev = new Event('mouseup', { bubbles: false });
        span?.dispatchEvent(ev);
        if (onMouseUp) {
          onMouseUp(e);
        }
      },
      [onMouseUp]
    );

    const classes = classList(styles, className, size, orientation);
    return (
      <Component
        {...props}
        className={classes}
        onMouseEnter={_onMouseEnter}
        onMouseLeave={_onMouseLeave}
        onMouseUp={_onMouseUp}
        aria-disabled={(props['aria-disabled'] ?? props.disabled) || undefined}
        ref={forwardedRef}
      >
        {iconLocation === 'Start' && icon}
        <Link as="span" className={classList({ inverse })}>
          {children}
        </Link>
        {iconLocation === 'End' && icon}
      </Component>
    );
  }
);
